import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { contactsAPI, contactsEndpoints } from '@/api/contacts';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import type { AccountDebt, DebtsResponse, UpdateDebtPayload } from '@/shared/types/contacts';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

interface UseDebtsParams {
  limit?: number;
  sort?: string;
  q?: string;
  status?: string;
  ptp_status?: string;
  has_flag?: string;
  has_party?: string;
  verbose?: string;
}

const EMPTY_PAGES: DebtsResponse[] = [];
const HAS_DEBTS_PARAMS: UseDebtsParams = { limit: 1 };

const buildSearchParams = (params: UseDebtsParams, cursor?: string) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      searchParams.set(key, String(value));
    }
  });
  if (cursor) {
    searchParams.set('next_cursor', cursor);
  }
  return searchParams;
};

export const useDebts = (params?: UseDebtsParams) => {
  const queryClient = useQueryClient();
  const slug = useSelector(selectAccountSlug);

  const { data, status, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery<DebtsResponse>({
      queryKey: ['debts/list', slug, params],
      queryFn: ({ pageParam }) =>
        contactsAPI.listAccountDebts(
          buildSearchParams({ ...params, verbose: 'true' }, pageParam as string | undefined)
        ),
      getNextPageParam: (lastPage) =>
        lastPage.pagination?.has_more ? lastPage.pagination.next_cursor : undefined,
      initialPageParam: undefined as string | undefined,
      enabled: !!params,
      placeholderData: keepPreviousData,
    });

  const deleteMutation = useMutation<void, Error, { debt: AccountDebt; force: boolean }>({
    mutationFn: ({ debt, force }) => contactsAPI.deleteDebt(debt.debt_id, force),
    onSuccess: (_, { debt }) => {
      queryClient.invalidateQueries({ queryKey: ['debts/list'] });
      debt.parties.forEach(({ contact_id }) => {
        queryClient.invalidateQueries({ queryKey: [contactsEndpoints.debts(contact_id)] });
      });
    },
  });

  const updateMutation = useMutation<
    AccountDebt,
    Error,
    { debt: AccountDebt; payload: UpdateDebtPayload }
  >({
    mutationFn: ({ debt, payload }) => contactsAPI.updateDebt(debt.debt_id, payload),
    onSuccess: (_, { debt }) => {
      queryClient.invalidateQueries({ queryKey: ['debts/list'] });
      debt.parties.forEach(({ contact_id }) => {
        queryClient.invalidateQueries({ queryKey: [contactsEndpoints.debts(contact_id)] });
        queryClient.invalidateQueries({ queryKey: [contactsEndpoints.activities(contact_id)] });
      });
      queryClient.invalidateQueries({ queryKey: [contactsEndpoints.debtEvents(debt.debt_id)] });
    },
    meta: { errorToast: true },
  });

  const pages = data?.pages ?? EMPTY_PAGES;
  const total = pages[0]?.pagination?.total;

  return {
    pages,
    total,
    status,
    error,
    refetch,
    fetchNextPage,
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    deleteDebt: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
    updateDebt: updateMutation.mutateAsync,
  };
};

export const useHasDebts = () => {
  const canRead = useSelector((state: RootState) =>
    getPermissions(state, 'contacts', actions.READ)
  );
  const slug = useSelector(selectAccountSlug);
  const { data } = useQuery({
    queryKey: ['debts/list', slug, 'exists'],
    queryFn: () => contactsAPI.listAccountDebts(buildSearchParams(HAS_DEBTS_PARAMS)),
    select: (response: DebtsResponse) => response.debts.length > 0,
    enabled: canRead,
  });
  return !!data;
};
